import { useCallback, useEffect, useMemo } from 'react';
import { useStore } from 'zustand';

import { useReferenceAssistantGateway } from './ReferenceAssistantGatewayProvider';
import { refetchCitationRequests } from '../api/citationRequestCache';
import {
  isUnexpectedTerminalStatus,
  type CitationRequest,
  type ProposedReference,
} from '../model/citationRequest';
import type {
  AttachedStatementReference,
  ExtractedStatement,
  StatementReferenceHelpRequest,
} from '../model/extractedStatements';
import {
  getCitationRequestReviewStore,
  selectApprovedReferenceIds,
  selectErrorMessage,
  selectHasCandidates,
  selectHasUnpublishedChanges,
  selectIsDoneState,
  selectIsOpen,
  selectIsPublishDisabled,
  selectIsPublishing,
  selectPublishCount,
} from '../state/citationRequestReviewStore';
import {
  getStatementTriageStore,
  selectDraftCount,
  selectHasUnpublishedDraftChanges,
} from '../state/statementTriageStore';

import { useChatAnalytics } from '@/hooks/chat/useChatAnalytics';
import { useTransitionEffect } from '@/hooks/useTransitionEffect';
import {
  trackCitationRequestApproved,
  trackCitationRequestApproveFailed,
  trackStatementReferenceHelpCanceled,
  trackStatementReferenceHelpRequested,
} from '@/util/analyticsEvents';
import { queryClient } from '@/util/queryClient';

interface UseCitationRequestReviewSheetInput {
  conversationId: string;
  citationRequest: CitationRequest;
  /** The message's current `attachedStatementReferences` array.
   * Synced into the store every render so the derived
   * `hasUnpublishedChanges` / `isDoneState` selectors stay live with
   * realtime updates to the parent message. Also used at publish time
   * to derive `freeTextReferences` — anything attached that isn't in
   * `citationRequest.proposedReferences` is forwarded so the backend
   * preserves it during its reconcile. */
  currentAttachedStatementReferences: readonly AttachedStatementReference[];
  /** The message's current `statementReferenceHelpRequests` array.
   * Drives the derived `hasUnpublishedDraftChanges` flag; the backend
   * owns the actual write so this is no longer sent with publish. */
  currentStatementReferenceHelpRequests?: readonly StatementReferenceHelpRequest[];
  /** The message's parsed `extractedStatements` array. Used to
   * resolve the `statement_type` analytics property for each help-
   * request event — `ProposedReference` does not carry the statement
   * type, so the hook needs the original pipeline payload to surface
   * a non-empty value to Segment. */
  extractedStatements?: readonly ExtractedStatement[];
}

/**
 * Behavior wrapper around `citationRequestReviewStore`. The store
 * owns the entire CTA state machine (`isPublishDisabled`,
 * `isDoneState`, `hasUnpublishedChanges`, `hasCandidates`); the hook
 * syncs the message's live references into the store, exposes a
 * `publishCta` packet that consolidates label + disabled + onPress,
 * and orchestrates async side effects
 * (`approveCitationRequest`, analytics, query invalidation). The
 * hook never cancels the prior citation request — when the expert
 * re-submits with a different selection, the new POST creates a new
 * request and the old one is orphaned in `awaiting_review` rather
 * than cancelled.
 *
 * # Persistent editable selection
 *
 * Publish is a *sync*, not a one-shot commit. The expert can keep
 * checking or unchecking candidates (and editing help-request
 * drafts) and tap Publish again to roll the change forward. The
 * approve call is idempotent on `published`, so each re-publish hits
 * the same endpoint; the backend reconciles
 * `attachedStatementReferences` and `statementReferenceHelpRequests`
 * against the supplied target state and returns the diff for
 * analytics. Free-text URLs the expert pasted via the inline `+`
 * chip are forwarded as `freeTextReferences` so the backend
 * preserves them.
 */
export function useCitationRequestReviewSheet({
  conversationId,
  citationRequest,
  currentAttachedStatementReferences,
  currentStatementReferenceHelpRequests,
  extractedStatements,
}: UseCitationRequestReviewSheetInput) {
  const store = getCitationRequestReviewStore(citationRequest.id);

  const gateway = useReferenceAssistantGateway();

  const { track } = useChatAnalytics();

  /**
   * IDs of this request's proposed references that are currently
   * attached to the parent message. Memoised so the sync effect only
   * fires when the underlying set actually changes.
   */
  const attachedFromThisRequestIds = useMemo(() => {
    const attachedKeys = new Set(
      currentAttachedStatementReferences.map((entry) => `${entry.statementIndex}::${entry.url}`)
    );

    return citationRequest.proposedReferences
      .filter((reference) => attachedKeys.has(`${reference.statementIndex}::${reference.url}`))
      .map((reference) => reference.id);
  }, [citationRequest.proposedReferences, currentAttachedStatementReferences]);

  useEffect(() => {
    store.getState().syncAttachedFromThisRequest(attachedFromThisRequestIds);
  }, [attachedFromThisRequestIds, store]);

  useEffect(() => {
    store.getState().syncProposedReferenceCount(citationRequest.proposedReferences.length);
  }, [citationRequest.proposedReferences.length, store]);

  /**
   * Subscribe to the per-message help-request store and reflect its
   * derived summary (non-empty draft count, unpublished-vs-persisted
   * diff) into the review store. Drives the publish CTA's count and
   * `hasUnpublishedChanges` so a help-request draft alone is enough
   * to enable Publish.
   *
   * Subscribes via `store.subscribe`, NOT `useStore`, so the hook is
   * stable across the re-renders that downstream rows produce when
   * the expert types — only the citation-review store re-renders
   * here (via `useStore` slices below), not the help-request store.
   */
  const helpRequestStore = useMemo(
    () => getStatementTriageStore(citationRequest.messageSid),
    [citationRequest.messageSid]
  );

  /**
   * Memoised, value-stable snapshot of `citationRequest.statementIndices`.
   * TanStack Query returns a new `citationRequest` object on every
   * poll/refetch, even when the payload is unchanged, so the raw
   * `citationRequest.statementIndices` array reference changes too.
   * Without this memo, the `pushSummary` effect below would unsubscribe
   * and resubscribe the help-request listener on every query refresh.
   * The serialized join is the dep key — order is significant for
   * universe scoping, so we don't sort.
   */
  const statementIndicesKey = citationRequest.statementIndices.join(',');

  const statementIndices = useMemo(
    () => citationRequest.statementIndices,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: re-stabilise when the joined key changes, not the array identity
    [statementIndicesKey]
  );

  useEffect(() => {
    const pushSummary = () => {
      const helpState = helpRequestStore.getState();

      const draftCount = selectDraftCount(helpState);

      const hasUnpublishedChanges = selectHasUnpublishedDraftChanges(
        helpState,
        currentStatementReferenceHelpRequests,
        statementIndices
      );

      store.getState().syncHelpRequestSummary({ draftCount, hasUnpublishedChanges });
    };

    pushSummary();

    return helpRequestStore.subscribe(pushSummary);
  }, [helpRequestStore, store, currentStatementReferenceHelpRequests, statementIndices]);

  const isOpen = useStore(store, selectIsOpen);

  const isPublishing = useStore(store, selectIsPublishing);

  const errorMessage = useStore(store, selectErrorMessage);

  const approvedReferenceIds = useStore(store, selectApprovedReferenceIds);

  const publishCount = useStore(store, selectPublishCount);

  const hasCandidates = useStore(store, selectHasCandidates);

  const hasUnpublishedChanges = useStore(store, selectHasUnpublishedChanges);

  const isDoneState = useStore(store, selectIsDoneState);

  const isPublishDisabled = useStore(store, selectIsPublishDisabled);

  /**
   * Auto-reset when the request terminates *unexpectedly* (`failed`,
   * `cancelled`) — those endings mean the workflow is done and the
   * local approval set no longer applies. `published` is intentionally
   * excluded; publish is a non-destructive sync (see hook docstring),
   * so the sheet stays open with the approval set intact.
   */
  useTransitionEffect(citationRequest.status, (previousStatus, nextStatus) => {
    if (isOpen && previousStatus === 'awaiting_review' && isUnexpectedTerminalStatus(nextStatus)) {
      store.getState().reset();
    }
  });

  const handlePublish = useCallback(async () => {
    const state = store.getState();

    const approvedIds = Array.from(state.approvedReferenceIds);

    const rejectedCount = citationRequest.proposedReferences.length - approvedIds.length;

    /**
     * `freeTextReferences` is the set of `(statementIndex, url)` pairs
     * the expert pasted via the inline `+` chip — i.e., currently
     * attached refs that did not originate from this citation
     * request. Forwarding them keeps the backend reconcile from
     * dropping them when it rewrites `attachedStatementReferences`.
     */
    const proposedKeys = new Set(
      citationRequest.proposedReferences.map(
        (reference) => `${reference.statementIndex}::${reference.url}`
      )
    );

    const freeTextReferences = currentAttachedStatementReferences
      .filter((entry) => !proposedKeys.has(`${entry.statementIndex}::${entry.url}`))
      .map((entry) => ({ statementIndex: entry.statementIndex, url: entry.url }));

    /**
     * `statementType` per statementIndex — drives the `statement_type`
     * analytics payload on every help-request event. The data lives
     * on `extractedStatements` (pipeline payload), not on
     * `ProposedReference`, so we have to read it from the parent
     * message's extracted statements directly. Falls back to `''`
     * when the caller doesn't supply `extractedStatements` (legacy
     * callers without help-request UI) or when a statementIndex is
     * out of range.
     */
    const statementTypeByIndex = new Map<number, string>();

    if (extractedStatements) {
      for (const statementIndex of citationRequest.statementIndices) {
        const statement = extractedStatements[statementIndex];

        statementTypeByIndex.set(statementIndex, statement?.statementType ?? '');
      }
    }

    /**
     * Target state for help requests scoped to this citation
     * request's statement indices. Empty `description` signals
     * "remove any existing entry"; the backend trims and validates,
     * so the client forwards the raw draft (or `''` when no draft is
     * present).
     */
    const helpState = helpRequestStore.getState();

    const helpRequests = citationRequest.statementIndices.map((statementIndex) => ({
      statementIndex,
      description: helpState.drafts.get(statementIndex)?.description ?? '',
    }));

    /**
     * Capture the pre-publish status so the `CITATION_REQUEST_APPROVED`
     * event only fires on the actual `awaiting_review → published`
     * transition. Re-publishes hit the same endpoint (idempotent on
     * `published`) but should not re-emit the approval event.
     */
    const wasAwaitingReview = citationRequest.status === 'awaiting_review';

    state.beginPublish();

    try {
      const { diff } = await gateway.approveCitationRequest({
        citationRequestId: citationRequest.id,
        approvedReferenceIds: approvedIds,
        freeTextReferences,
        helpRequests,
      });

      void refetchCitationRequests(queryClient, conversationId);

      if (wasAwaitingReview) {
        trackCitationRequestApproved(track, {
          citationRequestId: citationRequest.id,
          conversationId,
          messageSid: citationRequest.messageSid,
          approvedCount: approvedIds.length,
          rejectedCount,
        });
      }

      const totalStatements = citationRequest.statementIndices.length;

      const helpRequestDiff = diff.statementReferenceHelpRequests;

      for (const entry of [...helpRequestDiff.added, ...helpRequestDiff.updated]) {
        const proposedForStatement = citationRequest.proposedReferences.filter(
          (reference) => reference.statementIndex === entry.statementIndex
        );

        trackStatementReferenceHelpRequested(track, {
          conversationId,
          messageSid: citationRequest.messageSid,
          statementIndex: entry.statementIndex,
          statementType: statementTypeByIndex.get(entry.statementIndex) ?? '',
          totalStatements,
          description: entry.description,
          hadCandidates: proposedForStatement.length > 0,
          candidateCount: proposedForStatement.length,
        });
      }

      for (const entry of helpRequestDiff.removed) {
        trackStatementReferenceHelpCanceled(track, {
          conversationId,
          messageSid: citationRequest.messageSid,
          statementIndex: entry.statementIndex,
          statementType: statementTypeByIndex.get(entry.statementIndex) ?? '',
          totalStatements,
        });
      }

      store.getState().endPublish();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to publish references.';

      trackCitationRequestApproveFailed(track, {
        citationRequestId: citationRequest.id,
        conversationId,
        messageSid: citationRequest.messageSid,
        errorKind: 'persist_failed',
      });

      store.getState().endPublish(new Error(message));
    }
  }, [
    citationRequest,
    conversationId,
    currentAttachedStatementReferences,
    extractedStatements,
    gateway,
    helpRequestStore,
    store,
    track,
  ]);

  const handleDismiss = useCallback(() => {
    const state = store.getState();

    if (state.isPublishing) {
      return;
    }

    // Close the wizard overlay (the surface this hook now lives in)
    // AND clear the orphaned `isOpen` flag on the review store —
    // belt-and-braces while the review store still carries that
    // legacy field. Approval state is intentionally left intact so
    // the next pill-tap can re-seed from the message's attached
    // refs without a flash of empty selection.
    //
    // Triage-store coupling: this hook is wizard-only post-merge, and
    // `handleDismiss` directly calls `getStatementTriageStore` to close
    // the wizard overlay. If this hook is ever extracted to another
    // surface that doesn't use the triage wizard, this coupling will
    // need to be revisited (e.g. make the triage-store close an
    // optional callback rather than a hard dependency).
    state.setOpen(false);

    getStatementTriageStore(citationRequest.messageSid).getState().close();
  }, [store, citationRequest.messageSid]);

  const handleToggle = useCallback(
    (referenceId: string) => {
      store.getState().toggleApproval(referenceId);
    },
    [store]
  );

  const isApproved = useCallback(
    (reference: ProposedReference) => approvedReferenceIds.has(reference.id),
    [approvedReferenceIds]
  );

  // Label is constant ("Publish", never "Publish (N)") — the button stays
  // visible and communicates availability through its disabled state, like
  // Apple Mail's Send. Count badges read ambiguously when the diff is a
  // removal, so the visible selection state carries that meaning instead.
  const publishCta = useMemo(
    () => ({
      label: 'Publish',
      isDisabled: isPublishDisabled,
      onPress: isDoneState ? handleDismiss : handlePublish,
    }),
    [isPublishDisabled, isDoneState, handleDismiss, handlePublish]
  );

  return {
    isOpen,
    isPublishing,
    errorMessage,
    approvedReferenceIds,
    publishCount,
    hasCandidates,
    hasUnpublishedChanges,
    isDoneState,
    publishCta,
    handlePublish,
    handleDismiss,
    handleToggle,
    isApproved,
  };
}
